/**
 * Alpine component for the moderator cases page (tickets C4a/C4b, #3279).
 * Fetches the queue from GET /v1/mod/cases and each dossier from
 * GET /v1/mod/cases/{id} via window.ApiClient. Decisions post to
 * POST /v1/mod/cases/{id}/decision carrying the case version the pane
 * rendered; a 409 means the case moved (new evidence or a concurrent
 * decision) and the pane reloads for a fresh decision. Overturn posts to
 * POST /v1/mod/cases/{id}/overturn.
 *
 * All ids are strings (case and subject ids exceed Number.MAX_SAFE_INTEGER);
 * they are never parsed as numbers. Lucide icons in the dynamically rendered
 * detail are re-initialised after each render.
 */

const LANE_LABELS = {
    minor_safety: 'Minor-safety lane',
    low_confidence: 'Low-confidence lane',
    general: 'General lane',
};

const RESOLUTION_LABELS = {
    delete: 'Deleted',
    warn: 'Warned',
    suspend: 'Suspended',
    delete_photo: 'Photo removed',
    dismiss: 'Dismissed',
    deleted_externally: 'Gone',
    overturned: 'Overturned',
};

const ACTOR_LABELS = {
    automod: 'Automod',
    moderator: 'Moderator',
    system: 'System',
};

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg,#60a5fa,#2563eb)',
    'linear-gradient(135deg,#f472b6,#c026d3)',
    'linear-gradient(135deg,#fbbf24,#f97316)',
    'linear-gradient(135deg,#34d399,#059669)',
    'linear-gradient(135deg,#a78bfa,#7c3aed)',
    'linear-gradient(135deg,#f9a8d4,#db2777)',
    'linear-gradient(135deg,#94a3b8,#64748b)',
];

export function modCasesPage(config = {}) {
    return {
        preselectCaseId: String(config.preselectCaseId || ''),

        loading: true,
        loadError: '',
        needsHuman: [],
        deferred: [],
        resolvedRecent: [],
        filter: 'all',

        selectedId: '',
        detail: null,
        detailLoading: false,
        detailError: '',
        showDetailMobile: false,

        decisionBusy: false,
        decisionError: '',
        decisionNotice: '',
        openForm: '',
        decisionNote: '',
        deferWakeAt: '',
        warnWarningId: '',
        suspendReason: '',
        suspendDays: 7,
        deleteReason: '',
        deleteAgeAck: false,
        overturnReason: '',

        init() {
            this.loadQueue();
        },

        async loadQueue() {
            this.loading = true;
            this.loadError = '';
            try {
                const response = await window.ApiClient.request('/mod/cases');
                if (!response.ok) {
                    this.loadError = 'Could not load the cases queue (' + response.status + ').';
                    return;
                }
                const body = await response.json();
                this.needsHuman = body.needs_human || [];
                this.deferred = body.deferred || [];
                this.resolvedRecent = body.resolved_recent || [];

                if (this.preselectCaseId) {
                    const target = this.preselectCaseId;
                    this.preselectCaseId = '';
                    this.selectCase(target);
                }
            } catch (_e) {
                this.loadError = 'Could not load the cases queue.';
            } finally {
                this.loading = false;
            }
        },

        get totalOpen() {
            return this.needsHuman.length + this.deferred.length;
        },

        get visibleNeedsHuman() {
            return this.filter === 'all' || this.filter === 'needs' ? this.needsHuman : [];
        },

        get visibleDeferred() {
            return this.filter === 'all' || this.filter === 'deferred' ? this.deferred : [];
        },

        get visibleResolved() {
            return this.filter === 'all' || this.filter === 'resolved' ? this.resolvedRecent : [];
        },

        get isEmpty() {
            return this.needsHuman.length === 0
                && this.deferred.length === 0
                && this.resolvedRecent.length === 0;
        },

        get filteredEmpty() {
            return !this.isEmpty
                && this.visibleNeedsHuman.length === 0
                && this.visibleDeferred.length === 0
                && this.visibleResolved.length === 0;
        },

        setFilter(value) {
            this.filter = value;
        },

        async selectCase(id) {
            const caseId = String(id);
            this.selectedId = caseId;
            this.showDetailMobile = true;
            this.detail = null;
            this.detailError = '';
            this.decisionError = '';
            this.decisionNotice = '';
            this.resetDecisionForms();
            this.detailLoading = true;
            try {
                const response = await window.ApiClient.request('/mod/cases/' + encodeURIComponent(caseId));
                // A slower response for a case the moderator has already
                // navigated away from must not clobber the current pane:
                // decisions read the case id and version from `detail`.
                if (this.selectedId !== caseId) {
                    return;
                }
                if (!response.ok) {
                    this.detailError = response.status === 404
                        ? 'This case no longer exists.'
                        : 'Could not load this case (' + response.status + ').';
                    return;
                }
                const body = await response.json();
                if (this.selectedId !== caseId) {
                    return;
                }
                this.detail = body;
                this.primeDecisionDefaults();
                this.refreshIcons();
            } catch (_e) {
                if (this.selectedId === caseId) {
                    this.detailError = 'Could not load this case.';
                }
            } finally {
                if (this.selectedId === caseId) {
                    this.detailLoading = false;
                }
            }
        },

        /**
         * Seed form defaults from the loaded dossier's decision options, so
         * selects never sit on an empty value.
         */
        primeDecisionDefaults() {
            const options = this.decisionOptions;
            if (!this.overturnReason && (options.restore_reasons || []).length) {
                this.overturnReason = options.restore_reasons[0];
            }
        },

        backToList() {
            this.showDetailMobile = false;
            this.selectedId = '';
        },

        // ── Decisions (C4b) ──────────────────────────────────────────────

        get caseOpen() {
            return !!(this.detail && this.detail.case && this.detail.case.state !== 'resolved');
        },

        get canDecideNow() {
            return !!(this.detail && this.detail.can_decide && this.caseOpen && !this.detail.case.is_pending);
        },

        get canOverturnNow() {
            // can_overturn is server-computed: resolved automod deletes only.
            // A human delete is not overturnable from this surface.
            return !!(this.detail && this.detail.can_decide && this.detail.can_overturn);
        },

        get minDeferDate() {
            const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
            return tomorrow.toISOString().slice(0, 10);
        },

        get decisionOptions() {
            return (this.detail && this.detail.decision_options) || {};
        },

        toggleForm(kind) {
            this.decisionError = '';
            if (this.openForm === kind) {
                this.openForm = '';
                return;
            }
            this.openForm = kind;
            const options = this.decisionOptions;
            if (kind === 'warn' && !this.warnWarningId && (options.warnings || []).length) {
                this.warnWarningId = String(options.warnings[0].id);
            }
            if (kind === 'suspend') {
                if (!this.suspendReason) {
                    this.suspendReason = (options.suspend_reasons || [])[0] || '';
                }
                this.suspendDays = options.suspend_days_default || 7;
            }
            if (kind === 'delete' && !this.deleteReason) {
                const reasons = options.delete_reasons || [];
                this.deleteReason = reasons.indexOf('fake') !== -1 ? 'fake' : (reasons[0] || '');
            }
            if (kind === 'overturn' && !this.overturnReason) {
                this.overturnReason = (options.restore_reasons || [])[0] || '';
            }
            this.refreshIcons();
        },

        resetDecisionForms() {
            this.openForm = '';
            this.decisionNote = '';
            this.deferWakeAt = '';
            this.warnWarningId = '';
            this.suspendReason = '';
            this.suspendDays = 7;
            this.deleteReason = '';
            this.deleteAgeAck = false;
            this.overturnReason = '';
        },

        subjectHandle() {
            const subject = (this.detail && this.detail.subject) || {};
            return subject.username ? '@' + subject.username : ('#' + (subject.id || ''));
        },

        async postCase(path, payload, notice) {
            if (this.decisionBusy || !this.detail) {
                return;
            }
            this.decisionBusy = true;
            this.decisionError = '';
            this.decisionNotice = '';
            const caseId = String(this.detail.case.id);
            try {
                const response = await window.ApiClient.request(
                    '/mod/cases/' + encodeURIComponent(caseId) + path,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    },
                );
                if (response.status === 409) {
                    // The moderator may have navigated to another case while
                    // this was in flight: don't hijack the pane back.
                    if (String(this.selectedId) !== caseId) {
                        return;
                    }
                    let conflictCode = '';
                    try {
                        conflictCode = String((await response.json()).error.code || '');
                    } catch {
                        conflictCode = '';
                    }
                    // selectCase clears decisionError, so set the explanation
                    // after the reload or the moderator never sees it.
                    await this.selectCase(caseId);
                    this.decisionError = conflictCode === 'case_tenure_ack_required'
                        ? 'This account is well-established. Confirm the account-age acknowledgement first.'
                        : 'This case changed while you were looking at it. Reloaded, decide again.';
                    this.loadQueue();
                    return;
                }
                if (!response.ok) {
                    // A late failure for a case the moderator has since left
                    // must not surface an error over the pane they moved to.
                    if (String(this.selectedId) !== caseId) {
                        return;
                    }
                    this.decisionError = 'Decision failed (' + response.status + ').';
                    return;
                }
                const updated = await response.json();
                // A slower decision response for a case the moderator has since
                // navigated away from must not clobber the current pane.
                if (String(this.selectedId) !== caseId) {
                    return;
                }
                this.detail = updated;
                this.decisionNotice = notice;
                this.resetDecisionForms();
                this.loadQueue();
                this.refreshIcons();
            } catch (_e) {
                if (String(this.selectedId) !== caseId) {
                    return;
                }
                this.decisionError = 'Decision failed. Check your connection and retry.';
            } finally {
                this.decisionBusy = false;
            }
        },

        decisionPayload(resolution, extra) {
            const payload = Object.assign({
                version: this.detail.case.version,
                resolution,
            }, extra || {});
            const note = this.decisionNote.trim();
            if (note !== '') {
                payload.note = note;
            }
            return payload;
        },

        decideDismiss() {
            this.postCase('/decision', this.decisionPayload('dismiss'), 'Case dismissed.');
        },

        decideDefer() {
            const extra = {};
            if (this.deferWakeAt) {
                extra.wake_at = this.deferWakeAt + ' 00:00:00';
            }
            this.postCase('/decision', this.decisionPayload('defer', extra), 'Case deferred.');
        },

        decideWarn() {
            if (!this.warnWarningId) {
                this.decisionError = 'Pick a warning first.';
                return;
            }
            this.postCase(
                '/decision',
                this.decisionPayload('warn', { warning_id: this.warnWarningId }),
                'Warning sent. Case resolved.',
            );
        },

        decideSuspend() {
            if (this.decisionBusy) {
                return;
            }
            if (!this.suspendReason) {
                this.decisionError = 'Pick a reason first.';
                return;
            }
            const minDays = Number(this.decisionOptions.suspend_days_min) || 1;
            const maxDays = Number(this.decisionOptions.suspend_days_max) || 30;
            const days = Number(this.suspendDays);
            if (this.suspendDays === '' || this.suspendDays === null
                || !Number.isInteger(days) || days < minDays || days > maxDays) {
                this.decisionError = 'Suspension length must be between ' + minDays + ' and ' + maxDays + ' days.';
                return;
            }
            window.ConfirmModal.show({
                title: 'Suspend account',
                message: 'Suspend ' + this.subjectHandle() + ' for ' + this.suspendDays + ' days?',
                confirmText: 'Suspend',
                type: 'warning',
                onConfirm: () => this.postCase(
                    '/decision',
                    this.decisionPayload('suspend', {
                        reason_category: this.suspendReason,
                        duration_days: this.suspendDays,
                    }),
                    'Suspension applied. Case resolved.',
                ),
            });
        },

        decideDelete() {
            if (this.decisionBusy) {
                return;
            }
            if (!this.deleteReason) {
                this.decisionError = 'Pick a reason first.';
                return;
            }
            const tenure = this.detail && this.detail.delete_tenure;
            if (tenure) {
                if (!this.deleteAgeAck) {
                    this.decisionError = 'Confirm you understand this account is well-established.';
                    return;
                }
                if (tenure.requires_note && this.decisionNote.trim() === '') {
                    this.decisionError = 'Add a note explaining the deletion.';
                    return;
                }
            }
            const extra = { reason_category: this.deleteReason };
            if (tenure && this.deleteAgeAck) {
                extra.account_age_ack = true;
            }
            window.ConfirmModal.show({
                title: 'Delete account',
                message: 'Delete ' + this.subjectHandle() + '? The account is restorable for about a year'
                    + ' and the appeal window stays open.',
                confirmText: 'Delete account',
                type: 'danger',
                onConfirm: () => this.postCase(
                    '/decision',
                    this.decisionPayload('delete', extra),
                    'Deletion queued. The case resolves when it completes.',
                ),
            });
        },

        decideOverturn() {
            if (this.decisionBusy) {
                return;
            }
            const note = this.decisionNote.trim();
            if (note === '') {
                this.decisionError = 'An overturn needs a note saying why automod got it wrong.';
                return;
            }
            if (!this.overturnReason) {
                this.decisionError = 'Pick a restore reason first.';
                return;
            }
            window.ConfirmModal.show({
                title: 'Overturn deletion',
                message: 'Restore ' + this.subjectHandle() + ' and mark this case overturned?'
                    + ' The account gets automod immunity so the same signals do not re-delete it.',
                confirmText: 'Overturn',
                type: 'warning',
                onConfirm: () => this.postCase(
                    '/overturn',
                    { reason_category: this.overturnReason, note },
                    'Deletion overturned. Account restored.',
                ),
            });
        },

        isSelected(id) {
            return this.selectedId !== '' && String(id) === this.selectedId;
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons && this.$refs.detailHost) {
                    window.LucideIcons.initialize(this.$refs.detailHost);
                }
            });
        },

        // ── Presentation helpers ──────────────────────────────────────────

        laneLabel(lane) {
            return LANE_LABELS[lane] || 'General lane';
        },

        resolutionLabel(resolution) {
            return RESOLUTION_LABELS[resolution] || 'Resolved';
        },

        actorLabel(actor) {
            return ACTOR_LABELS[actor] || 'System';
        },

        reasonLabel(reason) {
            return String(reason || '').replace(/-/g, ' ');
        },

        avatarGradient(uid) {
            const key = String(uid || '');
            let sum = 0;
            for (let i = 0; i < key.length; i += 1) {
                sum += key.charCodeAt(i);
            }
            return AVATAR_GRADIENTS[sum % AVATAR_GRADIENTS.length];
        },

        initial(subject) {
            const name = (subject && (subject.username || subject.name)) || '?';
            return name.charAt(0).toUpperCase();
        },

        sexSymbol(sex) {
            if (sex === 'f') {
                return '♀';
            }
            if (sex === 'm') {
                return '♂';
            }
            return '';
        },

        /**
         * Right-hand state pill for a list row. Returns { label, cls } where cls
         * is the Tailwind colour set for the badge.
         */
        stateBadge(item) {
            if (item.state === 'needs_human') {
                return {
                    label: 'Decide',
                    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                };
            }
            if (item.state === 'deferred') {
                return {
                    label: 'Deferred',
                    cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                };
            }
            return this.resolutionBadge(item.resolution);
        },

        resolutionBadge(resolution) {
            const label = (this.resolutionLabel(resolution) || 'Resolved').toUpperCase();
            let cls = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
            if (resolution === 'delete') {
                cls = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
            } else if (resolution === 'warn' || resolution === 'suspend' || resolution === 'delete_photo') {
                cls = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
            } else if (resolution === 'deleted_externally') {
                cls = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
            } else if (resolution === 'overturned') {
                cls = 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300';
            }
            return { label, cls };
        },

        accountStatusLabel(status) {
            if (status === 'deleted') {
                return 'Deleted account';
            }
            if (status === 'frozen') {
                return 'Suspended account';
            }
            return 'Active account';
        },

        relTime(iso) {
            if (!iso) {
                return '';
            }
            const then = Date.parse(iso);
            if (Number.isNaN(then)) {
                return '';
            }
            const diff = Math.max(0, Date.now() - then);
            const mins = Math.floor(diff / 60000);
            if (mins < 1) {
                return 'just now';
            }
            if (mins < 60) {
                return mins + 'm ago';
            }
            const hours = Math.floor(mins / 60);
            if (hours < 24) {
                return hours + 'h ago';
            }
            const days = Math.floor(hours / 24);
            if (days < 30) {
                return days + 'd ago';
            }
            return this.formatDate(iso);
        },

        formatDate(iso) {
            if (!iso) {
                return '';
            }
            const ts = Date.parse(iso);
            if (Number.isNaN(ts)) {
                return '';
            }
            return new Date(ts).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        },

        formatDateTime(iso) {
            if (!iso) {
                return '';
            }
            const ts = Date.parse(iso);
            if (Number.isNaN(ts)) {
                return '';
            }
            return new Date(ts).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
    };
}
